import { Container, IDestroyOptions, Rectangle, Sprite, Text, TEXT_GRADIENT, TextStyle } from "pixi.js";
import { Event } from "../../../../../../../../shared/event-system/Event";
import { ITokenFrame } from "./ITokenFrame";
import { MyWalletResourcePackage } from "../../../../resources/MyWalletResourcePackage";
import { ButtonWithScaleAndPosYAnimation } from "../../../../../../common/components/ButtonWithScaleAndPosYAnimation";
import { GameFont } from "../../../../../../common/GameFont";
import { CommonResourcesPackage } from "../../../../../../common/resources/CommonResourcesPackage";
import { HBox } from "../../../../../../common/components/ui/HBox";
import { ButtonWithScaleAnimation } from "../../../../../../common/components/ButtonWithScaleAnimation";
import gsap from "gsap";
import { AdaptiveTextInput, InputState } from "../../../../../../common/components/input/AdaptiveTextInput";
import { ICanvasScaler } from "../../../../../../../../canvas/ICanvasScaler";
import { Web3Model } from "../../../../../../../../web3/models/Web3Model";
import { nameof } from "../../../../../../../../shared/bindable-data";
import { IMyWalletController } from "../../../../controllers/IMyWalletController";
import { StringFormatExtention } from "../../../../../../../../extensions/StringFormatExtention";
import { GameInfo, Stage } from "../../../../../../../../analytics/GameInfo";
import { Chain } from "thirdweb";

export class SendErc20Frame extends Container implements ITokenFrame {
    private static readonly ADDRESS_PLACEHOLDER_TEXT = "| RECIPIENT ADDRESS";
    private static readonly AMOUNT_PLACEHOLDER_TEXT = "| AMOUNT";
    private static readonly BALANCE_TEXT = "Balance: ";

    public get onBackButtonClick(): Event<void> {
        return <Event<void>><any>this.backButton.onUp;
    }

    private readonly addressRegex = /^0x([a-fA-F0-9]{40})/;
    private readonly addressEditRegex = /^0x([a-fA-F0-9]{0,40})/;
    private readonly amountEditRegex = /^([0-9]{0,8})/;

    private readonly erc20Address: string;
    private readonly chain: Chain;
    private readonly myWalletController: IMyWalletController;
    private readonly web3Model: Web3Model;
    private readonly backButton: ButtonWithScaleAnimation;
    private readonly background: Sprite;
    private readonly addressInput: AdaptiveTextInput;
    private readonly amountInput: AdaptiveTextInput;
    private readonly balanceText: Text;
    private readonly balanceHBox: HBox;
    private readonly balanceValueText: Text;
    private readonly tokenTypeText: Text;
    private readonly sendButton: ButtonWithScaleAndPosYAnimation;
    private readonly sendButtonText: Text;
    private readonly gameInfo: GameInfo;
    private readonly showTimeline: gsap.core.Timeline;

    private amountPreviousInput: string;
    private addressPreviousInput: string;
    private isValidAmount: boolean;
    private isValidAddress: boolean;
    private minAmount: number;
    private showed: boolean;
    private processingTransfer: boolean;

    public constructor(
        chain: Chain,
        erc20Address: string,
        tokenName: string,
        commonResources: CommonResourcesPackage,
        myWalletResources: MyWalletResourcePackage,
        myWalletController: IMyWalletController,
        web3Model: Web3Model,
        canvasScaler: ICanvasScaler,
        gameInfo: GameInfo
    ) {
        super();
        this.myWalletController = myWalletController;
        this.web3Model = web3Model;
        this.chain = chain;
        this.erc20Address = erc20Address;
        this.gameInfo = gameInfo;
        this.showed = false;
        this.processingTransfer = false;

        this.minAmount = this.getMinAmount();

        this.backButton = new ButtonWithScaleAnimation(commonResources.assets_common_ui_back_png);
        this.backButton.defaultView.height = 52;

        this.background = new Sprite(myWalletResources.assets_views_my_wallet_ui_mon_send_frame_png);
        this.background.position.set(-67, -5);
        this.backButton.position.set(this.background.position.x - this.backButton.width - 28, this.background.position.y + 11);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height * 0.5, this.backButton.width * 2, this.backButton.height * 2);

        const addressInputBackground = new Sprite(myWalletResources.assets_views_my_wallet_ui_input_field_png);
        this.addressInput = new AdaptiveTextInput({
            scaler: canvasScaler,
            background: addressInputBackground,
            inputPaddings: {
                left: 32,
                top: 20,
                right: 32
            },
            placeholderText: SendErc20Frame.ADDRESS_PLACEHOLDER_TEXT,
            textStyle: new PlaceholderInputStyle()
        });
        this.addressInput.position.set(this.background.position.x + 70, this.background.position.y + 74);
        this.addressInput.inputTextView.alpha = 0.4;

        const amountInputBackground = new Sprite(myWalletResources.assets_views_my_wallet_ui_input_field_png);
        this.amountInput = new AdaptiveTextInput({
            scaler: canvasScaler,
            background: amountInputBackground,
            inputPaddings: {
                left: 32,
                top: 20,
                right: 32
            },
            placeholderText: SendErc20Frame.AMOUNT_PLACEHOLDER_TEXT,
            textStyle: new PlaceholderInputStyle()
        });
        this.amountInput.position.set(this.addressInput.position.x, this.addressInput.position.y + this.addressInput.height + 40);
        this.amountInput.inputTextView.alpha = 0.4;

        this.balanceHBox = new HBox(5);
        this.balanceText = new Text(SendErc20Frame.BALANCE_TEXT, new BalanceTextStyle());

        this.balanceValueText = new Text(this.formatBalance(this.getBalanceOfCorrectToken(chain)), new BalanceValueTextStyle());
        this.tokenTypeText = new Text(tokenName, new BalanceTextStyle());

        this.balanceHBox.addChild(
            this.balanceText,
            this.balanceValueText,
            this.tokenTypeText
        );
        this.balanceHBox.position.set(this.background.position.x + (this.background.width - this.balanceHBox.width) * 0.5, this.amountInput.position.y + this.amountInput.height + 50);

        this.sendButton = new ButtonWithScaleAndPosYAnimation(commonResources.assets_common_ui_button_green_without_shadow_png);
        this.sendButton.width = 218;
        this.sendButton.position.set(this.background.position.x + (this.background.width - this.sendButton.width) * 0.5, this.balanceHBox.position.y + this.balanceHBox.height + 51);

        this.sendButtonText = new Text("SEND", new SendButtonTextStyle());
        this.sendButtonText.position.set(80, 22);
        this.sendButton.defaultView.addChild(this.sendButtonText);

        this.showTimeline = new gsap.core.Timeline({ paused: true });

        this.addChild(
            this.backButton,
            this.background,
            this.addressInput,
            this.amountInput,
            this.balanceHBox,
            this.sendButton
        );

        this.amountPreviousInput = "";
        this.isValidAmount = false;
        this.isValidAddress = false;

        this.addressInput.onTextChanged.add(this.onAdressInputChange, this);
        this.addressInput.onStateChanged.add(this.onAddressStateChanged, this);
        this.amountInput.onTextChanged.add(this.onAmountInputChange, this);
        this.amountInput.onStateChanged.add(this.onAmountStateChange, this);
        this.sendButton.onUp.add(this.onSendButtonClick, this);
        this.web3Model.propertyChanged.add(this.onWeb3ModelPropertyChange, this);

        this.updateButtonAvaliability();
    }

    private getMinAmount(): number {
        switch (this.gameInfo.serverStage) {
            case Stage.LOCALHOST:
            case Stage.DEVELOPMENT: {
                switch (this.erc20Address.toLowerCase()) {
                    case this.web3Model.getUsdcTokenAddress().toLowerCase(): return 2;
                    case this.web3Model.getMonTokenAddress().toLowerCase(): return 1;
                }
            };
            case Stage.STAGE:
            case Stage.STAGING: {
                switch (this.erc20Address.toLowerCase()) {
                    case this.web3Model.getUsdcTokenAddress().toLowerCase(): return 5;
                    case this.web3Model.getMonTokenAddress().toLowerCase(): return 10;
                }
            };
            case Stage.PRODUCTION: {
                switch (this.erc20Address.toLowerCase()) {
                    case this.web3Model.getUsdcTokenAddress().toLowerCase(): return 25;
                    case this.web3Model.getMonTokenAddress().toLowerCase(): return 300;
                }
            };
        }
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.showTimeline.kill();
        this.addressInput.onTextChanged.remove(this.onAdressInputChange, this);
        this.addressInput.onStateChanged.remove(this.onAddressStateChanged, this);
        this.amountInput.onTextChanged.remove(this.onAmountInputChange, this);
        this.amountInput.onStateChanged.remove(this.onAmountStateChange, this);
        this.sendButton.onUp.remove(this.onSendButtonClick, this);
        this.web3Model.propertyChanged.remove(this.onWeb3ModelPropertyChange, this);
        super.destroy(options);
    }

    public async show(): Promise<void> {
        this.showTimeline.clear();
        this.showTimeline.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.15 });
        await this.showTimeline.play();
        this.addressInput.showHtmlInput();
        this.amountInput.showHtmlInput();
    }

    public async hide(): Promise<void> {
        this.addressInput.hideHtmlInput();
        this.amountInput.hideHtmlInput();
        await this.showTimeline.reverse();
    }

    private formatBalance(value: string): string {
        const numValue = Number(value);
        return isNaN(numValue) || numValue === 0 ? "0.0000" : StringFormatExtention.tokensFormat(value);
    }

    private onAdressInputChange(_text: string): void {
        if (this.addressInput.value.length > 1) {
            if (this.addressInput.value[0] !== "0") {
                this.addressInput.value = `0x${this.addressInput.value}`;
            }
            else if (this.addressInput.value[1] !== "x") {
                this.addressInput.value = this.addressPreviousInput;
            }
        }
        if (this.addressInput.value.length > 0 && this.addressInput.value[0] !== "0") {
            this.addressInput.value = `0x${this.addressInput.value}`;
        }
        if (this.addressInput.value?.[0] === "0" && this.addressInput.value?.[1] === "x") {
            this.addressInput.value = this.addressEditRegex.test(this.addressInput.value) ? this.addressInput.value.match(this.addressEditRegex)[0] : this.addressPreviousInput;
            this.isValidAddress = this.addressRegex.test(this.addressInput.value);
            this.addressPreviousInput = this.addressInput.value;
        }
        this.updateButtonAvaliability();
    }

    private onAddressStateChanged(state: InputState): void {
        const fill = (state == InputState.EDITING || this.addressInput.value.length === 0 || this.isValidAddress) ? "#FFFFFF" : "#ff4f4f";
        this.addressInput.defaultTextStyle.fill = fill;
        this.addressInput.inputTextView.style.fill = fill;
        this.addressInput.updateInputStyle();
    }

    private onAmountInputChange(_text: string): void {
        const balance = Math.floor(Number(this.getBalanceOfCorrectToken(this.chain)));
        if (this.amountInput.value.length > 0) {
            this.amountInput.value = this.amountEditRegex.test(this.amountInput.value) ? String(Number(this.amountInput.value.match(this.amountEditRegex)[0])) : this.amountPreviousInput;
            if (Number(this.amountInput.value) > balance) {
                this.amountInput.value = `${balance}`;
            }
        }
        else {
            this.amountInput.value = "";
        }
        this.amountPreviousInput = this.amountInput.value;
        this.isValidAmount = this.amountInput.value.length > 0 && Number(this.amountInput.value) >= this.minAmount;
        this.updateButtonAvaliability();
    }

    private onAmountStateChange(state: InputState): void {
        if (state == InputState.NONE) {
            this.onAmountInputChange(this.amountInput.value);
            const minAmount = String(this.minAmount);
            const balance = Math.floor(Number(this.getBalanceOfCorrectToken(this.chain)));
            if (this.amountInput.value.length != 0 && Number(this.amountInput.value) < Number(minAmount)) {
                this.amountInput.value = minAmount;
            }
            if (Number(this.amountInput.value) > balance) {
                this.amountInput.value = balance >= this.minAmount ? `${balance}` : "";
            }
            this.amountPreviousInput = this.amountInput.value;
            this.isValidAmount = this.amountInput.value.length > 0 && Number(this.amountInput.value) >= this.minAmount;
            this.updateButtonAvaliability();
        }
    }

    private async onSendButtonClick(): Promise<void> {
        if (!this.isValidAddress || !this.isValidAmount) {
            return;
        }
        this.processingTransfer = true;
        this.addressInput.hideHtmlInput();
        this.amountInput.hideHtmlInput();
        await this.myWalletController.sendErc20Tokens(this.chain, this.erc20Address, this.addressInput.value, Number(this.amountInput.value));
        this.processingTransfer = false;
        this.balanceValueText.text = this.formatBalance(this.web3Model.isTargetChain(this.chain) ? this.web3Model.erc20BalancesOnBase.get(this.erc20Address) : this.web3Model.erc20BalancesOnL1.get(this.erc20Address));
        this.addressInput.showHtmlInput();
        this.amountInput.showHtmlInput();
        this.addressInput.value = "";
        this.amountInput.value = "";
        this.isValidAmount = false;
        this.isValidAddress = false;
        this.updateButtonAvaliability();
    }

    private onWeb3ModelPropertyChange(model: Web3Model, key: string) {
        switch (key) {
            case nameof(model, "erc20BalancesOnBase"):
                this.balanceValueText.text = this.formatBalance(this.web3Model.erc20BalancesOnBase.get(this.erc20Address));
                this.balanceHBox.alignmentStrategy();
                this.balanceHBox.x = this.background.position.x + (this.background.width - this.balanceHBox.width) * 0.5
                break;
            case nameof(model, "erc20BalancesOnL1"):
                this.balanceValueText.text = this.formatBalance(this.web3Model.erc20BalancesOnL1.get(this.erc20Address));
                this.balanceHBox.alignmentStrategy();
                this.balanceHBox.x = this.background.position.x + (this.background.width - this.balanceHBox.width) * 0.5
                break;
        }
    }

    private getBalanceOfCorrectToken(chain: Chain): string {
        if (this.web3Model.isTargetChain(chain)) {
            return this.web3Model.erc20BalancesOnBase.get(this.erc20Address);
        } else {
            return this.web3Model.erc20BalancesOnL1.get(this.erc20Address);
        }
    }

    private updateButtonAvaliability(): void {
        const tint = (this.isValidAddress && this.isValidAmount) ? "#ffffff" : "#4E544E";
        this.sendButton.defaultView.tint = tint;
        this.sendButtonText.tint = tint;
        this.sendButton.enabled = this.isValidAddress && this.isValidAmount;
    }
}

class SendButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 57,
            fontWeight: "400",
            lineHeight: 71,
            fill: "#FFFFFF",
            dropShadow: true,
            dropShadowAlpha: 0.4,
            dropShadowAngle: 0.6,
            dropShadowBlur: 4,
            dropShadowDistance: 4
        });
    }
}

class PlaceholderInputStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 42,
            fontWeight: '400',
            fill: "#FFFFFF"
        });
    }
}

class BalanceTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48,
            fill: "#FFFFFF"
        });
    }
}

class BalanceValueTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48,
            fill: ["#AAFD09", "#16CF15"],
            fillGradientType: TEXT_GRADIENT.LINEAR_VERTICAL
        });
    }
}
